import React, { useState, useRef, useEffect } from "react";
import Layout from "@/components/Layout";
import { UploadCloud, FileText, Loader2, CheckCircle2, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const PHASE_LABEL = {
  queued: "Queued",
  extracting: "AI reading your PO",
  gemini: "AI reading your PO",
  building: "Building line items",
  done: "Done",
  error: "Error",
};

const okTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];

const JOB_POLL_INTERVAL_MS = 2500;
const MAX_UPLOAD_SIZE_MB = 50;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

const rid = () => `f_${Math.random().toString(36).slice(2, 9)}`;

export default function POUpload() {
  // items: [{ _rid, file, status, jobId?, phase?, po_id?, error?, elapsed }]
  const [items, setItems] = useState([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const [tick, setTick] = useState(0);

  const anyActive = items.some((it) => it.status === "queued" || it.status === "processing");

  // Tick every 1s to refresh elapsed timers
  useEffect(() => {
    if (!anyActive) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [anyActive]);

  // Poll active jobs
  useEffect(() => {
    if (!anyActive) { if (pollRef.current) clearInterval(pollRef.current); return; }
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const jobs = items.filter((it) => it.status === "processing" && it.jobId);
      if (!jobs.length) return;
      const updates = await Promise.all(jobs.map(async (it) => {
        try {
          const res = await api.get(`/po/jobs/${it.jobId}`);
          return { _rid: it._rid, data: res.data };
        } catch { return null; }
      }));
      setItems((prev) => prev.map((it) => {
        const u = updates.find((x) => x && x._rid === it._rid);
        if (!u) return it;
        if (u.data.status === "done") {
          toast.success(`${it.file.name}: ${u.data.line_item_count} lines decoded`);
          return { ...it, status: "done", phase: "done", po_id: u.data.po_id, lineCount: u.data.line_item_count };
        }
        if (u.data.status === "error") {
          toast.error(`${it.file.name}: ${u.data.error || "Extraction failed"}`);
          return { ...it, status: "error", error: u.data.error };
        }
        return { ...it, phase: u.data.phase || u.data.status };
      }));
    }, JOB_POLL_INTERVAL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [anyActive, items]);

  const addFiles = (fileList) => {
    if (!fileList?.length) return;
    const now = Date.now();
    const toAdd = [];
    for (const f of Array.from(fileList)) {
      if (!okTypes.includes(f.type)) { toast.error(`${f.name}: unsupported file type`); continue; }
      if (f.size > MAX_UPLOAD_SIZE_BYTES) { toast.error(`${f.name}: too large (max ${MAX_UPLOAD_SIZE_MB} MB)`); continue; }
      toAdd.push({ _rid: rid(), file: f, status: "ready", phase: null, startedAt: now });
    }
    if (toAdd.length) setItems((prev) => [...prev, ...toAdd]);
  };

  const removeItem = (id) => setItems((prev) => prev.filter((it) => it._rid !== id));

  const startUploadAll = async () => {
    const pending = items.filter((it) => it.status === "ready");
    if (!pending.length) return;
    // Mark all as queued, then upload in parallel
    setItems((prev) => prev.map((it) => it.status === "ready" ? { ...it, status: "queued", startedAt: Date.now() } : it));
    await Promise.all(pending.map(async (it) => {
      try {
        const form = new FormData();
        form.append("file", it.file);
        const res = await api.post("/po/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 5 * 60 * 1000,
        });
        setItems((prev) => prev.map((x) => x._rid === it._rid ? { ...x, status: "processing", jobId: res.data.job_id, phase: "queued" } : x));
      } catch (e) {
        setItems((prev) => prev.map((x) => x._rid === it._rid ? { ...x, status: "error", error: e?.response?.data?.detail || "Upload failed" } : x));
      }
    }));
  };

  const secsSince = (startedAt) => Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const _t = tick;  // eslint-disable-line no-unused-vars

  return (
    <Layout title="Upload Purchase Order" subtitle="Drop one or many POs — PDF or image. We queue each on the server so you can leave this tab open.">
      <div className="max-w-4xl">
        <div
          data-testid="po-upload-zone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer flex flex-col items-center justify-center gap-3 ${
            dragging ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white hover:border-slate-400 hover:bg-gray-50"
          }`}
        >
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
            <UploadCloud className="text-slate-600" size={26} />
          </div>
          <div>
            <div className="font-display text-lg font-semibold text-slate-900">Drop one or more POs here</div>
            <div className="text-sm text-gray-500 mt-1">or click to browse — PDF, PNG, JPG, WEBP (up to {MAX_UPLOAD_SIZE_MB} MB each, unlimited count)</div>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="application/pdf,image/*"
            className="hidden"
            data-testid="po-file-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
        </div>

        {items.length > 0 && (
          <div className="mt-6 bg-white border border-gray-200 rounded-md">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="font-display text-base font-semibold text-slate-900">Queue ({items.length})</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setItems([])}
                  disabled={anyActive}
                  className="text-xs text-gray-600 border border-gray-200 rounded-md px-3 py-1.5 hover:bg-gray-50 disabled:opacity-40"
                >Clear</button>
                <button
                  onClick={startUploadAll}
                  disabled={anyActive || !items.some((it) => it.status === "ready")}
                  data-testid="po-decode-btn"
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-1.5 text-sm font-medium transition-colors inline-flex items-center gap-2 disabled:opacity-40"
                >
                  {anyActive ? (<><Loader2 className="animate-spin" size={14} /> Processing...</>) : (<><CheckCircle2 size={14} /> Decode all ({items.filter((it) => it.status === "ready").length})</>)}
                </button>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {items.map((it) => (
                <div key={it._rid} className="p-4 flex items-center gap-4" data-testid={`po-queue-item-${it._rid}`}>
                  <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="text-slate-600" size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{it.file.name}</div>
                    <div className="text-[11px] text-gray-500 font-mono">
                      {(it.file.size / 1024).toFixed(1)} KB
                      {it.status === "processing" && ` · ${secsSince(it.startedAt)}s · ${PHASE_LABEL[it.phase] || "working"}`}
                      {it.status === "done" && ` · ${it.lineCount} lines`}
                      {it.status === "error" && ` · ${it.error}`}
                    </div>
                    {(it.status === "queued" || it.status === "processing") && (
                      <div className="h-1 mt-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-slate-900" style={{
                          width: it.phase === "queued" ? "20%" : (it.phase === "gemini" || it.phase === "extracting") ? "60%" : it.phase === "building" ? "90%" : "35%",
                          transition: "width 0.6s ease",
                        }} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {it.status === "ready" && <span className="text-[10px] uppercase tracking-widest text-gray-400 font-mono">ready</span>}
                    {it.status === "queued" && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">queued</span>}
                    {it.status === "processing" && <Loader2 className="animate-spin text-slate-500" size={14} />}
                    {it.status === "done" && (
                      <Link to={`/orders/${it.po_id}`} className="text-xs text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-md px-2.5 py-1 hover:bg-emerald-100 inline-flex items-center gap-1">
                        Open <ArrowRight size={12} />
                      </Link>
                    )}
                    {it.status === "error" && <span className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">error</span>}
                    {(it.status === "ready" || it.status === "done" || it.status === "error") && (
                      <button onClick={() => removeItem(it._rid)} className="text-gray-400 hover:text-red-600" data-testid={`remove-${it._rid}`}>
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 bg-white border border-gray-200 rounded-md p-5">
          <div className="text-xs uppercase tracking-widest font-mono text-gray-500 mb-2">Tip</div>
          <p className="text-sm text-gray-700">Multi-file uploads process all files in parallel on the server. You can close this tab — the extraction continues in the background, and finished POs appear in <Link to="/orders" className="underline">Purchase Orders</Link>.</p>
        </div>
      </div>
    </Layout>
  );
}
